/**
 * As lojas do motor de sincronizacao, montadas sobre as tabelas que o nucleo ja
 * tem: `_outbox`, `_conflicts`, `_meta` e a tabela de cada entidade.
 *
 * Este arquivo e a raiz de composicao a fazer o seu trabalho: e o unico lugar
 * autorizado a conhecer os DOIS lados — as tabelas Dexie do `@flos/core` e as
 * portas do `@flos/sync`. Ver a excecao `COMPOSITION_ROOT` em
 * `tools/architecture.test.ts`. O motor continua sem importar o nucleo, e o
 * nucleo continua sem saber que existe sincronizacao.
 *
 * A decisao que mais importa aqui esta em `DexieRecordStore.applyRemote`.
 *
 * Um registro que chega do servidor **nao passa pelo `Repository`**. Nao e uma
 * escrita dela: e a reposicao de algo que ja foi decidido e ja foi auditado
 * noutro aparelho. Passa-lo pelo repositorio teria tres consequencias, todas
 * más — e a primeira e a que lhe faz mesmo mal:
 *
 *  1. o repositorio exige `<plugin>:write`, e o modo leitura-apenas (sessao
 *     expirada, sem rede) passaria a impedir tambem o *pull* — ela deixava de
 *     receber o que a familia escreveu, exatamente quando mais precisa;
 *  2. gerava uma entrada de auditoria por cada registro recebido, dizendo que
 *     ela alterou coisas em que nunca tocou;
 *  3. voltava a por na outbox o registro que acabamos de RECEBER, criando um
 *     ciclo de envio infinito.
 *
 * Por isso escrevemos diretamente na tabela e emitimos `RECORD_CHANGED` com
 * `source: 'sync'` a mao — que e precisamente o que a porta `RecordStore` manda
 * fazer. A autoridade sobre permissoes no caminho de sincronizacao e o
 * servidor: o backend verifica os escopos no push e no pull.
 */
import { CoreEvents, type RecordChangedPayload, type SyncState } from '@flos/shared';
import type { Table } from 'dexie';
import type { FlosCore } from '@flos/core';
import type {
  ConflictStoreLike,
  CursorStoreLike,
  OutboxRetryUpdateLike,
  OutboxStoreLike,
  RecordStoreLike,
  SnapshotProviderLike,
  StoredConflictLike,
  StoredOutboxItemLike,
  SyncRecordLike,
} from './ports';

/** Cursor do ultimo pull bem sucedido. Vive em `_meta`, nao numa tabela nova. */
export const SYNC_CURSOR_META_KEY = 'sync.cursor';
/** Identificador deste aparelho. Nao e segredo, mas tambem nao sai daqui. */
export const DEVICE_ID_META_KEY = 'sync.deviceId';

/**
 * Ordem FIFO exigida pela porta `OutboxStore`: por `queuedAt` e, em caso de
 * empate, pelo `id` — que e um ULID, logo tambem ele temporal.
 */
function fifo(a: StoredOutboxItemLike, b: StoredOutboxItemLike): number {
  if (a.queuedAt === b.queuedAt) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  return a.queuedAt < b.queuedAt ? -1 : 1;
}

/**
 * Copia deliberada de `isEligible` do `@flos/sync`.
 *
 * Nao importamos a funcao porque `@flos/sync` e um pacote OPCIONAL, carregado em
 * tempo de execucao (`modules.ts`): um import estatico daqui poria o pacote no
 * grafo do arranque e um pacote partido passava a derrubar o aplicativo inteiro.
 * `dexieStores.test.ts` compara as duas implementacoes para nunca divergirem.
 */
function eligible(item: StoredOutboxItemLike, nowMs: number): boolean {
  if (item.poisoned) return false;
  if (!item.nextAttemptAt) return true;
  const at = Date.parse(item.nextAttemptAt);
  return Number.isNaN(at) || at <= nowMs;
}

// --- Fila de envio -----------------------------------------------------------

export class DexieOutboxStore implements OutboxStoreLike {
  readonly #queue: Table<StoredOutboxItemLike, string>;

  constructor(core: FlosCore) {
    // `_outbox` esta declarada como `Table<OutboxItem>`; `StoredOutboxItemLike`
    // acrescenta-lhe so campos locais (`poisoned`...), que nunca vao para a rede.
    this.#queue = core.db.outbox as unknown as Table<StoredOutboxItemLike, string>;
  }

  /**
   * Le a fila toda e ordena em memoria em vez de confiar no indice `queuedAt`.
   * O indice ordena por `queuedAt` mas nao promete nada sobre empates, e a fila
   * de uma familia sao dezenas de itens — nao vale a pena trocar correccao por
   * uma optimizacao que ninguem consegue medir.
   */
  async take(limit: number, now: string): Promise<StoredOutboxItemLike[]> {
    if (limit <= 0) return [];
    const at = Date.parse(now);
    const rows = await this.#queue.toArray();
    return rows
      .filter((item) => eligible(item, at))
      .sort(fifo)
      .slice(0, limit);
  }

  /**
   * Confirmado pelo servidor: sai da fila.
   *
   * Isto e uma remocao fisica, e e a correta. Um item de `_outbox` nao e um
   * registro de dominio — e um recibo de trabalho por fazer. O registro em si
   * continua na sua tabela, com a `AuditEntry` respectiva, e continua
   * reversivel (ADR-0004). Salvar a fila para sempre so duplicava payloads
   * (alguns deles de saude) sem acrescentar historico nenhum.
   *
   * `bulkDelete` e idempotente: ids que ja nao existem sao ignorados.
   */
  async ack(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    await this.#queue.bulkDelete(ids);
  }

  /** Contabilidade de tentativas. Nunca toca no `id` nem no `payload`. */
  async reschedule(updates: OutboxRetryUpdateLike[]): Promise<void> {
    if (updates.length === 0) return;
    await this.#queue.db.transaction('rw', this.#queue, async () => {
      for (const update of updates) {
        const item = await this.#queue.get(update.id);
        if (!item) continue;
        await this.#queue.put({
          ...item,
          attempts: update.attempts,
          lastError: update.lastError,
          nextAttemptAt: update.nextAttemptAt,
        });
      }
    });
  }

  /**
   * Parquear para revisao humana. O item SAI da fila de envio mas NAO e
   * apagado: a escrita dela continua salva e continua visivel.
   */
  async poison(ids: string[], reason: string): Promise<void> {
    if (ids.length === 0) return;
    const at = new Date().toISOString();
    await this.#queue.db.transaction('rw', this.#queue, async () => {
      for (const id of ids) {
        const item = await this.#queue.get(id);
        if (!item) continue;
        await this.#queue.put({
          ...item,
          poisoned: true,
          poisonedAt: at,
          poisonReason: reason,
        });
      }
    });
  }

  /** Decisao humana: voltar a tentar. Recomeca a contagem de tentativas. */
  async revive(id: string): Promise<void> {
    const item = await this.#queue.get(id);
    if (!item) return;
    const revived: StoredOutboxItemLike = { ...item, attempts: 0 };
    delete revived.poisoned;
    delete revived.poisonedAt;
    delete revived.poisonReason;
    delete revived.nextAttemptAt;
    await this.#queue.put(revived);
  }

  /**
   * Upsert por `id`, nunca duplicacao: as reposicoes de conflito reutilizam ids
   * deterministicos (`<raiz>~r<versao>`) e o servidor deduplica por esse id.
   * Chegar aqui duas vezes com o mesmo id tem de dar uma escrita, nao duas.
   */
  async enqueue(item: StoredOutboxItemLike): Promise<void> {
    await this.#queue.db.transaction('rw', this.#queue, async () => {
      const existing = await this.#queue.get(item.id);
      await this.#queue.put(existing ? { ...existing, ...item } : { ...item });
    });
  }

  /** Por enviar = tudo o que nao esta parqueado, inclusive o que espera backoff. */
  async countPending(): Promise<number> {
    return this.#queue.filter((item) => item.poisoned !== true).count();
  }

  async listPoisoned(): Promise<StoredOutboxItemLike[]> {
    const rows = await this.#queue.filter((item) => item.poisoned === true).toArray();
    return rows.sort(fifo);
  }
}

// --- Registros ----------------------------------------------------------------

export class DexieRecordStore implements RecordStoreLike {
  readonly #core: FlosCore;

  constructor(core: FlosCore) {
    this.#core = core;
  }

  /**
   * A tabela de uma entidade, ou `null` quando a entidade nao existe neste
   * aparelho — o caso normal de um plugin desligado nas configuracoes. Sem esta
   * guarda, `db.records()` lança e um unico registro de uma area desligada
   * derrubava o ciclo de sincronizacao inteiro.
   */
  #table(entity: string): Table<SyncRecordLike, string> | null {
    if (!this.#core.schema.has(entity)) return null;
    return this.#core.db.records<SyncRecordLike>(entity);
  }

  async get(entity: string, id: string): Promise<SyncRecordLike | null> {
    const table = this.#table(entity);
    if (!table) return null;
    return (await table.get(id)) ?? null;
  }

  /**
   * Aplica um registro vindo do servidor. Ver a nota no topo do arquivo: isto
   * NAO pode passar pelo `Repository`.
   */
  async applyRemote(entity: string, record: SyncRecordLike): Promise<void> {
    const table = this.#table(entity);
    if (!table) {
      // Nada se perde do lado dela: isto e conteudo do servidor, que la
      // continua. Volta a chegar quando a area for ligada e houver resync.
      this.#core.logger.warn('Registro recebido para uma area que nao esta ligada', {
        entity,
      });
      return;
    }

    const previous = await table.get(record.id);
    await table.put(record);

    const payload: RecordChangedPayload = {
      entity,
      recordId: record.id,
      operation: operationOf(previous ?? null, record),
      source: 'sync',
      record,
    };
    this.#core.events.emit(CoreEvents.RECORD_CHANGED, payload);
  }

  /**
   * Marca de contabilidade local (`pending`/`synced`/`conflict`). Nao emite
   * `RECORD_CHANGED` — de proposito, e a par com `MemoryRecordStore`: o
   * conteudo do registro nao mudou, e cada `ack` acordaria a interface toda por
   * nada. Quem mostra a fila ouve `SYNC_STATUS_CHANGED`.
   */
  async markSyncState(entity: string, id: string, state: SyncState): Promise<void> {
    const table = this.#table(entity);
    if (!table) return;
    const record = await table.get(id);
    if (!record) return;
    await table.put({ ...record, syncState: state });
  }
}

/** Que fato aconteceu ao registro, do ponto de vista de quem ouve o evento. */
function operationOf(
  previous: SyncRecordLike | null,
  next: SyncRecordLike,
): RecordChangedPayload['operation'] {
  const wasDeleted = previous?.['deletedAt'] != null;
  const isDeleted = next['deletedAt'] != null;
  if (!previous) return 'create';
  if (isDeleted && !wasDeleted) return 'delete';
  if (!isDeleted && wasDeleted) return 'restore';
  return 'update';
}

// --- Cursor ------------------------------------------------------------------

export class DexieCursorStore implements CursorStoreLike {
  readonly #core: FlosCore;

  constructor(core: FlosCore) {
    this.#core = core;
  }

  async get(): Promise<string | undefined> {
    const value = await this.#core.db.getMeta<string | undefined>(
      SYNC_CURSOR_META_KEY,
      undefined,
    );
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  async set(cursor: string): Promise<void> {
    await this.#core.db.setMeta(SYNC_CURSOR_META_KEY, cursor);
  }
}

// --- Conflitos ---------------------------------------------------------------

/**
 * Linha de `_conflicts` como fica gravada. A chave primaria da tabela e
 * `outboxItemId`, e `StoredConflictLike.id` e — por contrato — o mesmo valor.
 */
type ConflictRecord = StoredConflictLike & { outboxItemId: string; resolvedAt?: string | null };

export class DexieConflictStore implements ConflictStoreLike {
  readonly #table: Table<ConflictRecord, string>;

  constructor(core: FlosCore) {
    this.#table = core.db.conflicts as unknown as Table<ConflictRecord, string>;
  }

  async put(conflict: StoredConflictLike): Promise<void> {
    await this.#table.put({
      ...conflict,
      id: conflict.id,
      outboxItemId: conflict.outboxItemId,
      resolvedAt: null,
    });
  }

  async get(id: string): Promise<StoredConflictLike | null> {
    const row = await this.#table.get(id);
    // Um conflito ja decidido nao esta "por decidir": para o motor, nao existe.
    if (!row || row.resolvedAt) return null;
    return row;
  }

  async list(): Promise<StoredConflictLike[]> {
    const rows = await this.#table.toArray();
    return rows
      .filter((row) => !row.resolvedAt)
      .sort((a, b) => a.detectedAt.localeCompare(b.detectedAt));
  }

  /**
   * "Remover" um conflito e marca-lo como decidido, nao apaga-lo.
   *
   * A porta chama-lhe `remove` porque, do ponto de vista do motor, o conflito
   * deixa de existir. Mas isto e a memoria de uma decisao humana sobre dados
   * dela: apagar a linha destruia a prova de que houve divergencia e de que
   * lado ficou. Regra 6 / ADR-0004. `list()` e `count()` ja a ignoram, portanto
   * o comportamento observavel e identico ao de `MemoryConflictStore`.
   */
  async remove(id: string): Promise<void> {
    const row = await this.#table.get(id);
    if (!row) return;
    await this.#table.put({ ...row, resolvedAt: new Date().toISOString() });
  }

  async count(): Promise<number> {
    return this.#table.filter((row) => !row.resolvedAt).count();
  }
}

// --- Base da fusao a tres vias ----------------------------------------------

/**
 * Serve o estado ANTERIOR de um registro a partir do trilho de auditoria.
 *
 * Sem esta base, `classifyConflict` nao consegue saber QUEM alterou o que e
 * manda tudo para revisao humana. Com ela, duas pessoas a mexer em campos
 * diferentes do mesmo registro deixam de incomodar ninguem. A auditoria ja
 * guarda o `after` completo de cada versao (ADR-0004) — e exatamente o que
 * falta ao motor.
 */
export class AuditSnapshotProvider implements SnapshotProviderLike {
  readonly #core: FlosCore;

  constructor(core: FlosCore) {
    this.#core = core;
  }

  async snapshot(
    entity: string,
    recordId: string,
    version: number,
  ): Promise<Record<string, unknown> | null> {
    // Versao 0 e uma criacao: nao houve estado anterior nenhum.
    if (!Number.isFinite(version) || version <= 0) return null;
    try {
      const rows = await this.#core.db.audit
        .where('[entity+recordId]')
        .equals([entity, recordId])
        .toArray();
      for (const row of rows) {
        const after = row.after as Record<string, unknown> | null;
        if (after && after['version'] === version) return after;
      }
    } catch (error) {
      // Sem base a fusao fica conservadora (tudo vai a revisao). E pior para
      // ela, mas nunca perde nada — logo nao vale a pena travar o ciclo.
      this.#core.logger.debug('Nao consegui ler a base do registro na auditoria', {
        entity,
        erro: error instanceof Error ? error.message : String(error),
      });
    }
    return null;
  }
}

// --- Montagem ----------------------------------------------------------------

export interface DexieSyncStores {
  outbox: DexieOutboxStore;
  records: DexieRecordStore;
  cursor: DexieCursorStore;
  conflicts: DexieConflictStore;
  snapshots: AuditSnapshotProvider;
}

export function createDexieStores(core: FlosCore): DexieSyncStores {
  return {
    outbox: new DexieOutboxStore(core),
    records: new DexieRecordStore(core),
    cursor: new DexieCursorStore(core),
    conflicts: new DexieConflictStore(core),
    snapshots: new AuditSnapshotProvider(core),
  };
}

/**
 * Identificador estavel deste aparelho.
 *
 * O servidor usa-o para nao devolver a um aparelho as alteracoes que ele
 * proprio acabou de enviar. Vive em `_meta` e nao no `localStorage`: nao e um
 * segredo, mas tambem nao ha razao nenhuma para estar ao alcance de um script.
 */
export async function deviceId(core: FlosCore): Promise<string> {
  const stored = await core.db.getMeta<string>(DEVICE_ID_META_KEY, '');
  if (stored) return stored;
  const generated = `dev-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
  await core.db.setMeta(DEVICE_ID_META_KEY, generated);
  return generated;
}
